import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  increment,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { db, isTeacher } from '../../../utils/firebase';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import DropdownButton from '../../../components/ui/DropdownButton';
import AddStampTemplateDialog, {
  type StampTemplateFormData,
} from '../../../components/ui/AddStampTemplateDialog';
import GiveStampDialog from '../../../components/ui/GiveStampDialog';
import EditPencilIcon from '../../../components/icons/EditPencilIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';

interface StampTemplate {
  id: string;
  title: string;
  description: string;
  target: string;
  recipientCount: number;
  stampCount: number;
  createdAt: Date;
  gradeFilter: string;
  teacherId: string;
  subjectId: string;
  schoolId: string;
  studentIds: string[];
}

interface SubjectStudentRow {
  id: string;
  uid: string;
  name: string;
  number?: number;
  grade?: number;
  class?: number;
}

const gradeOptions = [
  { value: 'all', label: '전체 학년' },
  { value: '1', label: '1학년' },
  { value: '2', label: '2학년' },
  { value: '3', label: '3학년' },
];

const gradeLabel = (gradeFilter: string) => {
  if (gradeFilter === '1') return '1학년 전체';
  if (gradeFilter === '2') return '2학년 전체';
  if (gradeFilter === '3') return '3학년 전체';
  return '전체 학년';
};

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const StampPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [templates, setTemplates] = useState<StampTemplate[]>([]);
  const [students, setStudents] = useState<SubjectStudentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showGiveDialog, setShowGiveDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<StampTemplate | null>(null);
  const [givingTemplate, setGivingTemplate] = useState<StampTemplate | null>(null);
  const itemsPerPage = 6;

  const schoolId = currentUser && isTeacher(currentUser) ? currentUser.schoolId : '';
  const subjectId =
    currentUser && isTeacher(currentUser)
      ? currentUser.subjectId || currentUser.subject || ''
      : '';
  const teacherUid = currentUser?.uid || '';

  const templatesPath =
    schoolId && subjectId
      ? `schools/${schoolId}/subjects/${subjectId}/stampTemplates`
      : '';
  const studentsPath =
    schoolId && subjectId
      ? `schools/${schoolId}/subjects/${subjectId}/students`
      : '';
  const historiesPath =
    schoolId && subjectId
      ? `schools/${schoolId}/subjects/${subjectId}/stampHistories`
      : '';

  useEffect(() => {
    if (!templatesPath) {
      setTemplates([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, templatesPath),
      (snapshot) => {
        const rows = snapshot.docs.map((item) => {
          const data = item.data();
          const gradeFilter = String(data.gradeFilter || 'all');
          const studentIds = Array.isArray(data.studentIds) ? data.studentIds.map(String) : [];
          return {
            id: item.id,
            title: data.title || '',
            description: data.description || '',
            target: data.target || gradeLabel(gradeFilter),
            recipientCount: studentIds.length || Number(data.recipientCount) || 0,
            stampCount: Number(data.stampCount) || 1,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            gradeFilter,
            teacherId: data.teacherId || '',
            subjectId: data.subjectId || subjectId,
            schoolId: data.schoolId || schoolId,
            studentIds,
          } as StampTemplate;
        });
        rows.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setTemplates(rows);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('도장 템플릿 조회 실패:', err);
        setError('도장 템플릿을 불러오지 못했습니다.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [templatesPath, schoolId, subjectId]);

  useEffect(() => {
    if (!studentsPath) {
      setStudents([]);
      return;
    }

    const unsubscribe = onSnapshot(
      collection(db, studentsPath),
      (snapshot) => {
        const rows = snapshot.docs.map((item) => {
          const data = item.data();
          return {
            id: item.id,
            uid: data.uid || item.id,
            name: data.name || '이름 없음',
            number: data.number,
            grade: data.grade,
            class: data.class,
          } as SubjectStudentRow;
        });
        rows.sort((a, b) => {
          const gradeDiff = (a.grade || 0) - (b.grade || 0);
          if (gradeDiff !== 0) return gradeDiff;
          const classDiff = (a.class || 0) - (b.class || 0);
          if (classDiff !== 0) return classDiff;
          return (a.number || 0) - (b.number || 0);
        });
        setStudents(rows);
      },
      (err) => {
        console.error('교과 학생 조회 실패:', err);
      }
    );

    return () => unsubscribe();
  }, [studentsPath]);

  const filteredTemplates = useMemo(() => {
    if (selectedGrade === 'all') return templates;
    return templates.filter((template) => template.gradeFilter === selectedGrade);
  }, [templates, selectedGrade]);

  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const currentTemplates = filteredTemplates.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedGrade]);

  const giveTargetStudents = useMemo(() => {
    if (!givingTemplate) return [];
    if (givingTemplate.gradeFilter === 'all') return students;
    const gradeNum = Number(givingTemplate.gradeFilter);
    return students.filter((student) => Number(student.grade) === gradeNum);
  }, [givingTemplate, students]);

  const handleSaveTemplate = async (data: StampTemplateFormData) => {
    if (!templatesPath || !teacherUid || !schoolId || !subjectId) {
      throw new Error('교과 정보가 없습니다. 교과를 먼저 설정해 주세요.');
    }
    await addDoc(collection(db, templatesPath), {
      title: data.title,
      description: data.description,
      stampCount: data.stampCount,
      gradeFilter: data.gradeFilter,
      target: gradeLabel(data.gradeFilter),
      recipientCount: 0,
      studentIds: [],
      teacherId: teacherUid,
      subjectId,
      schoolId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const handleUpdateTemplate = async (data: StampTemplateFormData) => {
    if (!templatesPath || !editingTemplate) return;
    await updateDoc(doc(db, templatesPath, editingTemplate.id), {
      title: data.title,
      description: data.description,
      stampCount: data.stampCount,
      gradeFilter: data.gradeFilter,
      target: gradeLabel(data.gradeFilter),
      updatedAt: serverTimestamp(),
    });
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!templatesPath) return;
    if (!window.confirm('템플릿을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, templatesPath, templateId));
    } catch (err) {
      console.error('템플릿 삭제 실패:', err);
      alert('템플릿 삭제에 실패했습니다.');
    }
  };

  const handleGiveStamps = async (payload: {
    selectedStudentIds: string[];
    reason: string;
  }) => {
    if (!givingTemplate || !studentsPath || !historiesPath || !templatesPath) {
      throw new Error('교과 정보가 없습니다.');
    }

    const amount = givingTemplate.stampCount;
    const selected = students.filter((s) => payload.selectedStudentIds.includes(s.id));
    if (selected.length === 0) return;

    const batch = writeBatch(db);
    for (const student of selected) {
      const studentRef = doc(db, studentsPath, student.id);
      batch.update(studentRef, {
        'stampCount.count': increment(amount),
        updatedAt: serverTimestamp(),
      });

      const historyRef = doc(collection(db, historiesPath));
      batch.set(historyRef, {
        studentId: student.id,
        uid: student.uid,
        studentName: student.name,
        amount,
        reason: payload.reason,
        templateId: givingTemplate.id,
        templateTitle: givingTemplate.title,
        teacherId: teacherUid,
        teacherName: currentUser?.name || '',
        subjectId,
        schoolId,
        createdAt: serverTimestamp(),
      });
    }

    const mergedStudentIds = Array.from(
      new Set([...givingTemplate.studentIds, ...selected.map((s) => s.id)])
    );
    batch.update(doc(db, templatesPath, givingTemplate.id), {
      studentIds: mergedStudentIds,
      recipientCount: mergedStudentIds.length,
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    alert(`${selected.length}명에게 도장 ${amount}개를 지급했습니다.`);
  };

  if (!schoolId || !subjectId) {
    return (
      <div
        style={{
          width: '100%',
          minHeight: '100vh',
          background: 'var(--Color-background-main, #F4F6FA)',
          padding: '40px 24px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ maxWidth: 1280, margin: '0 auto', color: 'var(--Color-text-assistive)' }}>
          교과 정보가 없습니다. 교과 설정에서 담당 교과를 먼저 지정해 주세요.
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        background: 'var(--Color-background-main, #F4F6FA)',
        padding: '0 0 40px',
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 32,
        }}
      >
        <div
          style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: 32,
            fontFamily: 'Pretendard',
            fontWeight: 600,
            lineHeight: '48px',
          }}
        >
          도장 지급
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 20,
          }}
        >
          <DropdownButton
            options={gradeOptions}
            value={selectedGrade}
            onChange={setSelectedGrade}
            placeholder="전체 학년"
            size="medium"
            width="112px"
            height="40px"
          />
          <Button
            onClick={() => setShowAddDialog(true)}
            variant="submit"
            size="medium"
            width="118px"
            height="40px"
          >
            템플릿 추가
          </Button>
        </div>

        {error && (
          <div style={{ color: 'var(--Color-status-negative, #E52222)', fontSize: 14 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 40,
              color: 'var(--Color-text-assistive, #989898)',
              fontSize: 14,
            }}
          >
            로딩 중...
          </div>
        ) : currentTemplates.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 40,
              color: 'var(--Color-text-assistive, #989898)',
              fontSize: 14,
            }}
          >
            템플릿이 없습니다. 템플릿을 추가해주세요.
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              alignContent: 'flex-start',
            }}
          >
            {currentTemplates.map((template) => (
              <Card
                key={template.id}
                width="416px"
                height="auto"
                padding="16px 20px"
                borderRadius="12px"
                boxShadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
                background="var(--Color-background-normal, white)"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 40,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      color: 'var(--Color-text-minor, #BABABA)',
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: '18px',
                    }}
                  >
                    {formatDate(template.createdAt)} 생성
                  </div>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    <button
                      type="button"
                      aria-label="템플릿 수정"
                      onClick={() => {
                        setEditingTemplate(template);
                        setShowEditDialog(true);
                      }}
                      style={{
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                      }}
                    >
                      <EditPencilIcon width={16} height={16} />
                    </button>
                    <button
                      type="button"
                      aria-label="템플릿 삭제"
                      onClick={() => handleDeleteTemplate(template.id)}
                      style={{
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                      }}
                    >
                      <TrashIcon width={16} height={16} />
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                  <div
                    style={{
                      color: 'black',
                      fontSize: 18,
                      fontWeight: 600,
                      lineHeight: '27px',
                    }}
                  >
                    {template.title}
                  </div>
                  <div
                    style={{
                      color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                      fontSize: 13,
                      fontWeight: 500,
                      lineHeight: '19.5px',
                      wordBreak: 'break-word',
                    }}
                  >
                    {template.description}
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 20,
                    width: '100%',
                    minHeight: 104,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 20,
                      }}
                    >
                      <div style={{ color: 'var(--Color-text-assistive)', fontSize: 14, fontWeight: 500 }}>
                        지급 대상
                      </div>
                      <div style={{ color: 'var(--Color-text-normal)', fontSize: 14, fontWeight: 500 }}>
                        {template.target}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 20,
                      }}
                    >
                      <div style={{ color: 'var(--Color-text-assistive)', fontSize: 14, fontWeight: 500 }}>
                        도장 지급 인원
                      </div>
                      <div
                        style={{
                          color: 'var(--Color-text-brand-color, #FF6F0F)',
                          fontSize: 14,
                          fontWeight: 500,
                        }}
                      >
                        {template.recipientCount}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <div
                        style={{
                          color: 'var(--Color-text-brand-color, #FF6F0F)',
                          fontSize: 24,
                          fontWeight: 600,
                          lineHeight: '36px',
                        }}
                      >
                        {template.stampCount}
                      </div>
                      <div style={{ color: 'black', fontSize: 14, fontWeight: 500 }}>
                        도장
                      </div>
                    </div>
                    <Button
                      onClick={() => {
                        setGivingTemplate(template);
                        setShowGiveDialog(true);
                      }}
                      variant="submit"
                      size="small"
                      height="32px"
                      padding="8px 12px"
                    >
                      도장 지급
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {filteredTemplates.length > itemsPerPage && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 8,
              paddingTop: 20,
              paddingBottom: 20,
            }}
          >
            <div style={{ display: 'flex' }}>
              <div
                onClick={() => setCurrentPage(1)}
                style={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                  opacity: safePage === 1 ? 0.5 : 1,
                }}
              >
                <LeftArrowDoubleIcon width={24} height={24} color="#989898" />
              </div>
              <div
                onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                style={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                  opacity: safePage === 1 ? 0.5 : 1,
                }}
              >
                <LeftArrowIcon width={24} height={24} color="#989898" />
              </div>
            </div>

            <div style={{ display: 'flex' }}>
              {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 3) pageNum = i + 1;
                else if (safePage === 1) pageNum = i + 1;
                else if (safePage === totalPages) pageNum = totalPages - 2 + i;
                else pageNum = safePage - 1 + i;

                return (
                  <div
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color:
                        safePage === pageNum
                          ? 'var(--Color-text-normal, #161616)'
                          : 'var(--Color-text-assistive, #989898)',
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    {pageNum}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex' }}>
              <div
                onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
                style={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: safePage === totalPages ? 0.5 : 1,
                }}
              >
                <RightArrowIcon
                  width={24}
                  height={24}
                  color={safePage === totalPages ? '#989898' : '#161616'}
                />
              </div>
              <div
                onClick={() => setCurrentPage(totalPages)}
                style={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: safePage === totalPages ? 0.5 : 1,
                }}
              >
                <RightArrowDoubleIcon
                  width={24}
                  height={24}
                  color={safePage === totalPages ? '#989898' : '#161616'}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <AddStampTemplateDialog
        isOpen={showAddDialog}
        mode="create"
        onClose={() => setShowAddDialog(false)}
        onSave={handleSaveTemplate}
      />

      <AddStampTemplateDialog
        isOpen={showEditDialog}
        mode="edit"
        initialData={
          editingTemplate
            ? {
                title: editingTemplate.title,
                description: editingTemplate.description,
                stampCount: editingTemplate.stampCount,
                gradeFilter: editingTemplate.gradeFilter,
              }
            : null
        }
        onClose={() => {
          setShowEditDialog(false);
          setEditingTemplate(null);
        }}
        onSave={handleUpdateTemplate}
      />

      <GiveStampDialog
        isOpen={showGiveDialog}
        onClose={() => {
          setShowGiveDialog(false);
          setGivingTemplate(null);
        }}
        students={giveTargetStudents}
        templateTitle={givingTemplate?.title}
        templateDescription={givingTemplate?.description}
        stampCount={givingTemplate?.stampCount}
        onSave={handleGiveStamps}
      />
    </div>
  );
};

export default StampPage;
